import React from "react";

import { Stack } from "../layouts/stack/Stack";
import { Theme } from "../theme/Theme";
import { TooltipHost, TooltipHostProps } from "../surfaces/tooltip/TooltipHost";

export enum BadgeColor {
    ApplicationPrimary = 1,
    Blue = 2,
    LightBlue = 3,
    Green = 4,
    LightGreen = 5,
    Red = 6,
    Gray = 7,
    Yellow = 8
}

export interface BadgeProps {
    text: string;
    size?: number;
    color?: BadgeColor;
    testId?: string;
    tooltipContent?: TooltipHostProps["content"];
}

export const Badge: React.FunctionComponent<BadgeProps> = (props: BadgeProps) => {
    const { color, text, size, testId, tooltipContent } = props;

    const badgeSize = size ?? defaultBadgeSize;
    const badgeColor = getBadgeColor(color);

    const badge = (
        <Stack
            styles={{
                root: {
                    background: badgeColor,
                    borderRadius: badgeSize,
                    paddingLeft: badgeSize / 2,
                    paddingRight: badgeSize / 2,
                    paddingBottom: badgeSize / 12 + 2,
                    paddingTop: 2,
                    color: Theme.palette.white,
                    fontSize: badgeSize,
                    textAlign: "center",
                    width: "fit-content",
                    cursor: "default"
                }
            }}
            data-testId={testId}
        >
            {text}
        </Stack>
    );

    if (!tooltipContent) {
        return badge;
    }

    return <TooltipHost content={tooltipContent}>{badge}</TooltipHost>;
};

const getBadgeColor = (type?: BadgeColor) => {
    switch (type) {
        case BadgeColor.ApplicationPrimary:
            return Theme.palette.themePrimary;

        case BadgeColor.Blue:
            return Theme.palette.blue;

        case BadgeColor.LightBlue:
            return Theme.palette.blueLight;

        case BadgeColor.Green:
            return Theme.palette.green;

        case BadgeColor.LightGreen:
            return Theme.palette.greenLight;

        case BadgeColor.Red:
            return Theme.palette.red;

        case BadgeColor.Gray:
            return Theme.palette.neutralTertiary;

        case BadgeColor.Yellow:
            return Theme.palette.yellow;

        default:
            return defaultBadgeColor;
    }
};

const defaultBadgeSize = 12;
const defaultBadgeColor = Theme.palette.themePrimary;
